import { useState, useRef } from 'react';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useFormSubmit } from '../../../hooks/useFormSubmit';
import { useToast } from '../../../components/base/Toast';

const TradeInSchema = z.object({
  name: z.string().min(2, 'Please enter your full name'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(7, 'Please enter a valid phone number'),
  year: z.string().min(4, 'Please enter a valid year').max(4, 'Please enter a valid year'),
  make: z.string().min(1, 'Please enter the vehicle make'),
  model: z.string().min(1, 'Please enter the vehicle model'),
  mileage: z.string().min(1, 'Please enter the mileage'),
  condition: z.string().min(1, 'Please select a condition'),
  trim: z.string().optional(),
  vin: z.string().optional(),
  message: z.string().max(500, 'Message cannot exceed 500 characters').optional(),
});

export default function TradeInForm() {
  const navigate = useNavigate();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    year: '',
    make: '',
    model: '',
    trim: '',
    mileage: '',
    condition: '',
    vin: '',
    message: '',
  });

  const { handleSubmit, isSubmitting, validationErrors } = useFormSubmit({
    url: `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/trade-in-inquiry`,
    schema: TradeInSchema,
    formRef,
    onSuccess: () => navigate('/thank-you/trade-in'),
    onError: () => {
      setSubmitStatus('error');
      toast.error('Submission failed', 'Something went wrong. Please try again or call us directly.');
    },
    conversionLabel: 'TRADE_IN_LEAD_LABEL',
    leadEventName: 'Trade-In Form Submission',
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');
    await handleSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const fieldClass = (field: string) =>
    `w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A2332] focus:border-transparent transition-all text-sm ${
      validationErrors[field] ? 'border-red-400 bg-red-50' : 'border-[#E8E2D6]'
    }`;

  return (
    <div id="trade-in-form" className="bg-white rounded-2xl border border-[#E8E2D6] p-5 sm:p-8 shadow-sm">
      {/* Visually-hidden live region — announces submission state to screen readers */}
      <div
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {submitStatus === 'submitting' && 'Submitting your trade-in request, please wait.'}
        {submitStatus === 'error' && 'Submission failed. Please try again or call us directly.'}
      </div>

      <h2 className="font-['Oswald'] text-2xl sm:text-3xl font-semibold text-[#1A2332] mb-2">Get Your Trade-In Value</h2>
      <p className="text-[#4A5568] mb-6 sm:mb-8 text-sm">Provide your vehicle details and we'll give you a fair trade-in estimate within 24 hours.</p>

      <form onSubmit={onSubmit} className="space-y-5 sm:space-y-6" id="trade-in-form-el" data-readdy-form ref={formRef}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label htmlFor="name" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
              <i className="ri-user-line text-[#E8B84B]" aria-hidden="true"></i>Full Name
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.name} aria-describedby={validationErrors.name ? 'trade-in-name-error' : undefined} className={fieldClass('name')} placeholder="John Doe" autoComplete="name" />
            {validationErrors.name && <p id="trade-in-name-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.name}</p>}
          </div>
          <div>
            <label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
              <i className="ri-mail-line text-[#E8B84B]" aria-hidden="true"></i>Email Address
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.email} aria-describedby={validationErrors.email ? 'trade-in-email-error' : undefined} className={fieldClass('email')} placeholder="john@example.com" autoComplete="email" />
            {validationErrors.email && <p id="trade-in-email-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.email}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
            <i className="ri-phone-line text-[#E8B84B]" aria-hidden="true"></i>Phone Number
          </label>
          <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.phone} aria-describedby={validationErrors.phone ? 'trade-in-phone-error' : undefined} className={fieldClass('phone')} placeholder="(555) 123-4567" autoComplete="tel" />
          {validationErrors.phone && <p id="trade-in-phone-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.phone}</p>}
        </div>

        <div className="border-t border-[#E8E2D6] pt-5 sm:pt-6">
          <h3 className="font-['Oswald'] text-lg sm:text-xl font-semibold text-[#1A2332] mb-4">Vehicle Information</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            <div>
              <label htmlFor="year" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-calendar-line text-[#E8B84B]" aria-hidden="true"></i>Year
              </label>
              <input type="text" id="year" name="year" value={formData.year} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.year} aria-describedby={validationErrors.year ? 'trade-in-year-error' : undefined} className={fieldClass('year')} placeholder="2020" inputMode="numeric" />
              {validationErrors.year && <p id="trade-in-year-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.year}</p>}
            </div>
            <div>
              <label htmlFor="make" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-car-line text-[#E8B84B]" aria-hidden="true"></i>Make
              </label>
              <input type="text" id="make" name="make" value={formData.make} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.make} aria-describedby={validationErrors.make ? 'trade-in-make-error' : undefined} className={fieldClass('make')} placeholder="Toyota" />
              {validationErrors.make && <p id="trade-in-make-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.make}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            <div>
              <label htmlFor="model" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-roadster-line text-[#E8B84B]" aria-hidden="true"></i>Model
              </label>
              <input type="text" id="model" name="model" value={formData.model} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.model} aria-describedby={validationErrors.model ? 'trade-in-model-error' : undefined} className={fieldClass('model')} placeholder="Camry" />
              {validationErrors.model && <p id="trade-in-model-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.model}</p>}
            </div>
            <div>
              <label htmlFor="trim" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-settings-3-line text-[#E8B84B]" aria-hidden="true"></i>Trim Level
              </label>
              <input type="text" id="trim" name="trim" value={formData.trim} onChange={handleChange} className={fieldClass('trim')} placeholder="LE, SE, XLE, etc." />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label htmlFor="mileage" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-dashboard-line text-[#E8B84B]" aria-hidden="true"></i>Mileage
              </label>
              <input type="text" id="mileage" name="mileage" value={formData.mileage} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.mileage} aria-describedby={validationErrors.mileage ? 'trade-in-mileage-error' : undefined} className={fieldClass('mileage')} placeholder="50,000" inputMode="numeric" />
              {validationErrors.mileage && <p id="trade-in-mileage-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.mileage}</p>}
            </div>
            <div>
              <label htmlFor="condition" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-star-line text-[#E8B84B]" aria-hidden="true"></i>Condition
              </label>
              <select id="condition" name="condition" value={formData.condition} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.condition} aria-describedby={validationErrors.condition ? 'trade-in-condition-error' : undefined} className={fieldClass('condition')}>
                <option value="">Select condition</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
              {validationErrors.condition && <p id="trade-in-condition-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.condition}</p>}
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="vin" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
            <i className="ri-barcode-line text-[#E8B84B]" aria-hidden="true"></i>VIN (Optional)
          </label>
          <input type="text" id="vin" name="vin" value={formData.vin} onChange={handleChange} className={fieldClass('vin')} placeholder="1HGBH41JXMN109186" />
        </div>

        <div>
          <label htmlFor="message" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
            <i className="ri-message-3-line text-[#E8B84B]" aria-hidden="true"></i>Additional Details
          </label>
          <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} maxLength={500} aria-describedby={`trade-in-message-count${validationErrors.message ? ' trade-in-message-error' : ''}`} className={fieldClass('message')} placeholder="Any accidents, modifications, or special features?" />
          <div className="flex justify-between mt-1">
            {validationErrors.message ? <p id="trade-in-message-error" role="alert" className="text-red-500 text-xs">{validationErrors.message}</p> : <span />}
            <p id="trade-in-message-count" className="text-xs text-[#4A5568]" aria-live="polite">{formData.message.length}/500</p>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#1A2332] text-white py-3.5 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#2C3E55] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2"
        >
          {isSubmitting ? (
            <><i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>Submitting...</>
          ) : (
            <><i className="ri-send-plane-fill" aria-hidden="true"></i>Get Trade-In Value</>
          )}
        </button>
      </form>
    </div>
  );
}
